import { Box } from "@mui/material";
import React, { useState } from "react";
import SponsorshipInputCard from "./InputCard";
import SponsorshipDatePicker from "./DatePicker";
import SponsorshipLocationPicker from "./LocationPicker";
import SponsorshipTimePicker from "./TimePicker";
import SponsorshipClientInfoModal from "./ClientModal";
import SponsorshipEventDetailsModal from "./EventDetailsModal";
import SponsorshipThankYouModal from "./ThankYouModal";
import apiService from "../../../service/Api";

// Sponsorship Request Payload Interface
interface SponsorshipRequestPayload {
  request_type: "SPONSORSHIP";
  event: {
    title: string;
    event_flyer_image?: File | null;
  };
  contact: {
    first_name: string;
    last_name?: string;
    phone: string;
  };
  status: "PENDING";
  event_datetime: string;
  venue_name: string;
  venue_latitude: number;
  venue_longitude: number;
}

interface SponsorshipResponse {
  id: number;
  reference_number: string;
  event: {
    title: string;
  };
  contact: {
    first_name: string;
    last_name?: string;
    phone: string;
  };
  event_datetime: string;
  venue_name: string;
}

const buildSponsorshipFormData = (
  payload: SponsorshipRequestPayload,
  flyer?: File | null,
): FormData => {
  const formData = new FormData();

  formData.append("request_type", payload.request_type);
  formData.append("status", payload.status);
  formData.append("event.title", payload.event.title);

  if (flyer) {
    formData.append("event.event_flyer_image", flyer);
  }

  formData.append("contact.first_name", payload.contact.first_name);
  if (payload.contact.last_name) {
    formData.append("contact.last_name", payload.contact.last_name);
  }
  formData.append("contact.phone", payload.contact.phone);

  formData.append("event_datetime", payload.event_datetime);
  formData.append("venue_name", payload.venue_name);
  formData.append("venue_latitude", String(payload.venue_latitude));
  formData.append("venue_longitude", String(payload.venue_longitude));

  return formData;
};

interface SponsorshipRequestFormProps {
  buttonText?: string;
  buttonColor?: string;
  primaryColor?: string;
  secondaryColor?: string;
}

const SponsorshipRequestForm: React.FC<SponsorshipRequestFormProps> = ({
  buttonText = "Apply for Sponsorship",
  buttonColor = "#1976d2",
  primaryColor = "#1976d2",
  secondaryColor = "#f5f5f5",
}) => {
  const [formData, setFormData] = useState({
    when: "",
    where: "",
    whatTime: "",
    whatsappNumber: "",
    fullName: "",
    eventTitle: "",
    flyer: null as File | null,
  });

  const [currentModal, setCurrentModal] = useState<
    "clientInfo" | "eventDetails" | null
  >(null);
  const [flyerPreview, setFlyerPreview] = useState<string | null>(null);

  const [whenAnchor, setWhenAnchor] = useState<HTMLElement | null>(null);
  const [whereAnchor, setWhereAnchor] = useState<HTMLElement | null>(null);
  const [timeAnchor, setTimeAnchor] = useState<HTMLElement | null>(null);
  const [showThankYou, setShowThankYou] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState<string>("");
  const [eventTitle, setEventTitle] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const splitFullName = (fullName: string) => {
    const parts = fullName.trim().split(/\s+/);

    if (parts.length === 0) {
      return { first_name: "", last_name: "" };
    }

    if (parts.length === 1) {
      return { first_name: parts[0], last_name: "" };
    }

    return {
      first_name: parts.slice(0, -1).join(" "),
      last_name: parts[parts.length - 1],
    };
  };

  const normalizeName = (name: string) =>
    name
      .split(" ")
      .map((n) => n.charAt(0).toUpperCase() + n.slice(1).toLowerCase())
      .join(" ");

  // Reset form
  const resetForm = () => {
    setFormData({
      when: "",
      where: "",
      whatTime: "",
      whatsappNumber: "",
      fullName: "",
      eventTitle: "",
      flyer: null,
    });
    setFlyerPreview(null);
    setCurrentModal(null);
    setWhenAnchor(null);
    setWhereAnchor(null);
    setTimeAnchor(null);
  };

  // Step handlers
  const handleApplyForSponsorship = () => {
    if (!formData.when || !formData.where || !formData.whatTime) {
      alert("Please fill in all event details.");
      return;
    }
    setCurrentModal("clientInfo");
  };

  const handleClientInfoSubmit = () => {
    if (!formData.fullName.trim().includes(" ")) {
      alert("Please enter your full name.");
      return;
    }

    if (!formData.whatsappNumber.trim()) {
      alert("Please enter a valid WhatsApp number.");
      return;
    }
    // Go directly to event details (required for sponsorship)
    setCurrentModal("eventDetails");
  };

  const handleEventDetailsSubmit = async () => {
    if (isSubmitting) return;

    if (!formData.eventTitle.trim()) {
      alert("Please enter an event title.");
      return;
    }

    setIsSubmitting(true);

    const eventDatetime = `${formData.when}T${formData.whatTime}:00`;
    const { first_name, last_name } = splitFullName(formData.fullName);

    const payload: SponsorshipRequestPayload = {
      request_type: "SPONSORSHIP",
      event: {
        title: formData.eventTitle,
      },
      contact: {
        first_name: normalizeName(first_name),
        last_name: normalizeName(last_name || ""),
        phone: formData.whatsappNumber,
      },
      status: "PENDING",
      event_datetime: eventDatetime,
      venue_name: formData.where,
      venue_latitude: 0.0,
      venue_longitude: 0.0,
    };

    try {
      const multipartData = buildSponsorshipFormData(payload, formData.flyer);

      const response = await apiService.post<SponsorshipResponse>(
        "/api/bookings/quote-request/",
        multipartData,
      );

      setReferenceNumber(response.data.reference_number);
      setEventTitle(formData.eventTitle);

      setShowThankYou(true);
      resetForm();
    } catch (err: unknown) {
      console.error("Failed to submit sponsorship request", err);
      alert("Failed to submit sponsorship request");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFlyerUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert("Flyer must be under 10MB.");
        return;
      }
      setFormData({ ...formData, flyer: file });
      const reader = new FileReader();
      reader.onloadend = () => setFlyerPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const closeModal = () => setCurrentModal(null);

  const formatDate = (date: string) => {
    if (!date) return "Add dates";
    const d = new Date(date);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (time: string) => {
    if (!time) return "Add time";
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const whereBoxRef = React.useRef<HTMLDivElement>(null);
  const timeBoxRef = React.useRef<HTMLDivElement>(null);

  return (
    <Box>
      {/* Main Form Card */}
      <SponsorshipInputCard
        when={formData.when}
        where={formData.where}
        whatTime={formData.whatTime}
        whereBoxRef={whereBoxRef}
        timeBoxRef={timeBoxRef}
        onWhenClick={(el) => setWhenAnchor(el)}
        onWhereClick={(el) => setWhereAnchor(el)}
        onTimeClick={(el) => setTimeAnchor(el)}
        onApply={handleApplyForSponsorship}
        formatDate={formatDate}
        formatTime={formatTime}
        buttonText={buttonText}
        buttonColor={buttonColor}
      />

      {/* Custom Pickers */}
      <SponsorshipDatePicker
        open={Boolean(whenAnchor)}
        anchorEl={whenAnchor}
        onClose={() => setWhenAnchor(null)}
        value={formData.when}
        onChange={(date) => {
          setFormData({ ...formData, when: date });
          setWhenAnchor(null);
          if (whereBoxRef.current) {
            setWhereAnchor(whereBoxRef.current);
          }
        }}
        disablePast={true}
        showFlexibleDates={true}
      />

      <SponsorshipLocationPicker
        open={Boolean(whereAnchor)}
        anchorEl={whereAnchor}
        onClose={() => setWhereAnchor(null)}
        value={formData.where}
        onChange={(location) => {
          setFormData({ ...formData, where: location });
          setWhereAnchor(null);
          if (timeBoxRef.current) {
            setTimeAnchor(timeBoxRef.current);
          }
        }}
      />

      <SponsorshipTimePicker
        open={Boolean(timeAnchor)}
        anchorEl={timeAnchor}
        onClose={() => setTimeAnchor(null)}
        value={formData.whatTime}
        onChange={(time) => {
          setFormData({ ...formData, whatTime: time });
          setTimeAnchor(null);
        }}
        format="12h"
      />

      {/* Modals */}
      <SponsorshipClientInfoModal
        open={currentModal === "clientInfo"}
        onClose={closeModal}
        whatsappNumber={formData.whatsappNumber}
        onNumberChange={(val) =>
          setFormData((prev) => ({ ...prev, whatsappNumber: val }))
        }
        fullName={formData.fullName}
        onFullNameChange={(val) =>
          setFormData((prev) => ({ ...prev, fullName: val }))
        }
        onSubmit={handleClientInfoSubmit}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
      />

      <SponsorshipEventDetailsModal
        open={currentModal === "eventDetails"}
        onClose={closeModal}
        eventTitle={formData.eventTitle}
        onEventTitleChange={(val) =>
          setFormData((prev) => ({ ...prev, eventTitle: val }))
        }
        onFlyerUpload={handleFlyerUpload}
        flyerPreview={flyerPreview}
        onSubmit={handleEventDetailsSubmit}
        isSubmitting={isSubmitting}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
      />

      <SponsorshipThankYouModal
        open={showThankYou}
        onClose={() => {
          setShowThankYou(false);
          setCurrentModal(null);
          setReferenceNumber("");
          setEventTitle("");
        }}
        referenceNumber={referenceNumber}
        eventTitle={eventTitle}
        primaryColor={primaryColor}
      />
    </Box>
  );
};

export default SponsorshipRequestForm;
